import type {
  ChannelFeedEngagement,
  ExternalClientApp,
} from "@mlx/contracts";
import Link from "next/link";
import type { ComponentType, ReactNode } from "react";

import {
  ActorHoverView,
  type ActorIdentity,
} from "@/components/app/entities/actor-hover-view";
import {
  ProvenanceAvatarStack,
  type ProvenanceActor,
  type ProvenanceConversation,
  type ProvenanceInterface,
} from "@/components/app/runs/provenance-avatar-stack";
import {
  ContextChipView,
  type ContextChipType,
} from "@/components/app/sessions/context-chip-view";
import { sessionStatus } from "@/components/app/sessions/session-status";
import { ResourceIcon, type ResourceVisual } from "@/components/content/resource-card";
import { formatClockTime, formatDateTime, formatFeedTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { FeedRunAvatar } from "./feed-run-avatar";
import {
  FeedEngagementSummary,
  FeedRowActions,
  type FeedReactions,
} from "./feed-item-engagement";

/*
 * The feed row grammar as markup: an avatar carrying provenance, a sentence
 * that reads "actor verb subject", quiet metadata on the right with the row's
 * own actions beside the time, an optional body, then what the row has
 * gathered. FeedRow and FeedSentence keep the density context, the viewer's
 * reactions and the actor and chip cards, and render through these, so a
 * fixture row composes the same markup. No hooks: a Server Component can
 * draw a row, though reactions carry a handler, so a row that shows them is
 * drawn by a Client Component.
 */

export type FeedRowProvenance = {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  conversation?: ProvenanceConversation | null;
  executedByTwin?: boolean;
  /** Breathe the Twin mark for this long once the row is on screen. */
  workingSeconds?: number;
  /** The row's run is working: the Twin mark breathes in the avatar's place until it ends. */
  running?: boolean;
  action: "started" | "created" | "updated" | "posted";
};

export type FeedRowTone = "default" | "attention" | "closed" | "static";

/*
 * A surface that shows a few rows among other content (a channel Overview)
 * sets compact rows: a small avatar and tighter spacing that keep to the
 * rhythm of the tables beside them. Feeds keep the default.
 */
export type FeedRowDensity = "default" | "compact";

const TONE_CLASS: Record<FeedRowTone, string> = {
  default: "hover:bg-muted/30",
  attention: "border border-primary/20 bg-primary/5 hover:bg-primary/10",
  closed: "border border-border/30 opacity-60 hover:bg-muted/30",
  /* A row shown as a subject (a thread's root) rather than a stream entry. */
  static: "",
};

export type FeedRowViewProps = {
  actor: ProvenanceActor;
  provenance: FeedRowProvenance;
  occurredAt: number;
  unread?: boolean;
  older?: boolean;
  showConnector?: boolean;
  tone?: FeedRowTone;
  density?: FeedRowDensity;
  sentence: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
  /** Pass null for a row that can start a thread but has none yet. Omit for a row with no replies. */
  engagement?: ChannelFeedEngagement | null;
  /** The viewer's reactions, which the row's react button and gathered reactions toggle. */
  reactions?: FeedReactions | null;
  replyHref?: string;
  onThreadOpen?: () => void;
  /** Beside the time, before react and reply: a row's own actions. */
  actions?: ReactNode;
  /** A list item in a feed; a div among a thread's comments, which aren't a list. */
  as?: "li" | "div";
  /** Outside a feed's day groups (among a thread's comments), the time says its day as a comment's does. */
  dated?: boolean;
};

/** One feed row: the avatar, the sentence with the time and actions to its right, the body, then what it has gathered. */
export function FeedRowView({
  actor,
  provenance,
  occurredAt,
  unread = false,
  older = false,
  showConnector = false,
  tone = "default",
  density = "default",
  sentence,
  body,
  footer,
  engagement,
  reactions = null,
  replyHref,
  onThreadOpen,
  actions,
  as: Row = "li",
  dated = false,
}: FeedRowViewProps) {
  const compact = density === "compact";
  // A compact row with a body under its sentence takes more room than a one-line row.
  const roomy = compact && Boolean(body);
  const engages =
    reactions !== null || engagement !== undefined || replyHref !== undefined;
  return (
    <Row
      className={cn(
        "group relative rounded-lg px-2 transition-colors duration-150",
        // 16px between compact one-line rows, so they read apart; a row with a body, or a
        // bordered row (a Twin card), keeps more room between its text and its edge.
        compact ? (roomy ? "py-3" : tone === "attention" || tone === "closed" ? "py-2.5" : "py-2") : "py-3",
        TONE_CLASS[tone],
      )}
    >
      {showConnector ? (
        <span
          aria-hidden
          className={cn(
            "absolute w-px bg-border/70",
            // From under this row's avatar to the next row's, whose disc covers the overlap.
            compact
              ? cn("bottom-[-12px] left-[17.5px]", roomy ? "top-[34px]" : "top-[30px]")
              : "bottom-[-12px] left-[23px] top-10",
          )}
        />
      ) : null}
      <div className={cn("relative flex min-w-0 items-start", compact ? "gap-2.5" : "gap-3")}>
        {/* A compact avatar carries its source as a smaller corner badge. */}
        <FeedRunAvatar
          running={provenance.running ?? false}
          // The person's avatar with the Twin mark breathing beside it, as other avatar stacks show them.
          working={
            <ProvenanceAvatarStack
              actor={actor}
              executedByTwin
              working={provenance.running ?? false}
              action={provenance.action}
              size={compact ? "sm" : "md"}
              layout="stack"
              className={cn("shrink-0 self-start", compact ? "" : "mt-0.5")}
            />
          }
        >
        <ProvenanceAvatarStack
          actor={actor}
          clientApp={provenance.clientApp}
          interfaceType={provenance.interfaceType}
          conversation={provenance.conversation}
          executedByTwin={provenance.executedByTwin}
          workingSeconds={provenance.workingSeconds}
          action={provenance.action}
          size={compact ? "sm" : "md"}
          layout="overlay"
          className={cn("shrink-0 self-start", compact ? "w-5" : "mt-0.5 w-8")}
        />
        </FeedRunAvatar>
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3">
            <div className="min-w-0">{sentence}</div>
            {/* As tall as the sentence's first line, so the time sits level with it whether or not the row has actions. */}
            <span className="flex h-6 items-center gap-1.5 text-xs whitespace-nowrap tabular-nums text-muted-foreground">
              {actions}
              {engages ? (
                <FeedRowActions reactions={reactions} href={replyHref} onOpen={onThreadOpen} />
              ) : null}
              {older ? (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-2xs">
                  Older
                </span>
              ) : null}
              {unread ? (
                <span
                  aria-label="Unread"
                  className="size-1.5 rounded-full bg-primary"
                />
              ) : null}
              <time
                dateTime={new Date(occurredAt).toISOString()}
                title={formatDateTime(occurredAt)}
              >
                {dated ? formatFeedTime(occurredAt) : formatClockTime(occurredAt)}
              </time>
            </span>
          </div>
          {body ? <div className={compact ? "mt-0.5" : "mt-1"}>{body}</div> : null}
          {footer}
          {engages ? (
            <FeedEngagementSummary
              engagement={(reactions ? reactions.engagement : engagement) ?? null}
              reactions={reactions}
              onOpen={onThreadOpen}
              href={replyHref}
            />
          ) : null}
        </div>
      </div>
    </Row>
  );
}

/**
 * Under a row that stands for others like it: "+43 more record changes", or "+1 more record
 * change". The label is the plural ("record changes", "runs").
 */
export function FeedMoreLine({ count, label }: { count: number; label: string }) {
  return (
    <p className="text-xs leading-5 text-muted-foreground">
      +{count} more {count === 1 ? label.replace(/s$/u, "") : label}
    </p>
  );
}

export type FeedSentenceLink = {
  label: string;
  href?: string | null;
  /** The mark of the resource the link names, before its name: a canvas's. */
  visual?: ResourceVisual;
};

/** What opens an actor's card from their name; closed, it renders as ActorHoverView. */
export type FeedActorHoverComponent = ComponentType<{
  actor: ActorIdentity;
  className?: string;
  children: ReactNode;
}>;

/** What names a channel or Data Set in a sentence; closed, it renders as ContextChipView. */
export type FeedContextChipComponent = ComponentType<{
  type: ContextChipType;
  label: string;
  href?: string | null;
  className?: string;
}>;

export type FeedSentenceViewProps = {
  actor: string;
  /** Who the actor is; their name then opens their card. */
  actorIdentity?: ActorIdentity | null;
  /** Twin's work for a person names them after Twin: "Twin, for you, updated …". */
  onBehalfOf?: string | null;
  verb?: string | null;
  subject?: FeedSentenceLink | null;
  status?: string | null;
  /** A session's state as it stands: a working run's Twin mark breathes; a finished run says nothing. */
  runStatus?: string | null;
  /** An updated or archived record's changes, after a colon. */
  changes?: ReactNode;
  /** A created record's field count, after a dot. */
  createdFieldCount?: number;
  dataSet?: FeedSentenceLink | null;
  channel?: FeedSentenceLink | null;
  trailing?: ReactNode;
  /** A header that names the subject renders the sentence as its heading; a
   *  one-line row inside a button renders it as a span. */
  as?: "p" | "h2" | "div" | "span";
  className?: string;
  actorHover?: FeedActorHoverComponent;
  contextChip?: FeedContextChipComponent;
};

/*
 * The sentence every feed row reads as: actor, verb, subject, then status,
 * record changes, the Data Set chip, a created record's field count and the
 * channel chip in that order. A surface inside a channel omits the channel
 * chip; the cross-channel Feed page keeps it.
 */
export function FeedSentenceView({
  actor,
  actorIdentity,
  onBehalfOf,
  verb,
  subject,
  status,
  runStatus,
  changes,
  createdFieldCount = 0,
  dataSet,
  channel,
  trailing,
  as: Tag = "p",
  className,
  actorHover: ActorName = ActorHoverView,
  contextChip: Chip = ContextChipView,
}: FeedSentenceViewProps) {
  return (
    <Tag
      className={cn(
        "min-w-0 break-words text-sm leading-6 text-muted-foreground",
        className,
      )}
    >
      {actorIdentity ? (
        <ActorName actor={actorIdentity} className="font-medium text-foreground hover:underline hover:underline-offset-4">
          {actor}
        </ActorName>
      ) : (
        <span className="font-medium text-foreground">{actor}</span>
      )}
      {onBehalfOf ? `, for ${onBehalfOf},` : null}
      {verb ? ` ${verb}` : null}
      {subject ? (
        <>
          {" "}
          <SentenceLink link={subject} />
        </>
      ) : null}
      {status ? (
        <span className="text-tertiary-foreground">{` · ${status}`}</span>
      ) : null}
      {runStatus ? <FeedRunStatus status={runStatus} /> : null}
      {changes ? (
        <>
          {": "}
          {changes}
        </>
      ) : null}
      {dataSet ? (
        <>
          {" · "}
          <Chip
            type="dataSet"
            label={dataSet.label}
            href={dataSet.href}
          />
        </>
      ) : null}
      {createdFieldCount > 0
        ? ` · ${createdFieldCount} ${createdFieldCount === 1 ? "field" : "fields"}`
        : null}
      {channel ? (
        <>
          {" in "}
          <Chip
            type="channel"
            label={channel.label}
            href={channel.href ?? "/workspace/channels"}
          />
        </>
      ) : null}
      {trailing}
    </Tag>
  );
}

const RUN_STATUS_TONE: Record<string, string> = {
  Failed: "text-destructive",
  "Needs you": "text-primary",
};

/* " · running", " · failed" in red; a completed run reads as its sentence alone. A working
   run's Twin mark breathes in the row's avatar (FeedRunAvatar), not here. */
function FeedRunStatus({ status }: { status: string }) {
  const state = sessionStatus(status);
  if (state.label === "Completed") return null;
  return (
    <span className={RUN_STATUS_TONE[state.label] ?? "text-tertiary-foreground"}>
      {` · ${state.label.toLowerCase()}`}
    </span>
  );
}

function SentenceLink({ link }: { link: FeedSentenceLink }) {
  const className =
    "font-medium text-foreground underline-offset-4 hover:underline";
  const label = link.visual ? (
    <>
      <ResourceIcon visual={link.visual} className="mr-1 inline-block size-3.5 align-[-2px]" />
      {link.label}
    </>
  ) : link.label;
  return link.href ? (
    <Link href={link.href} className={className}>
      {label}
    </Link>
  ) : (
    <span className={className}>{label}</span>
  );
}
